import { FindBestPrice } from "@/api/interfaces";

export const RESULTS_PER_PAGE = 6;
export const DAYS_SUPPLY_OPTIONS = ["30", "90"];

const ZIP_REGEX = /^\d{5}$/;
const CLAIM_STATUS_PAID = "P";
const BASIS_OF_REIMBURSEMENT_USUAL_AND_CUSTOMARY = "04";

const IRREGULAR_PLURALS: Record<string, string> = {
  box: "boxes",
  suppository: "suppositories",
};

export interface CheckCoverageSearch {
  drug: FindBestPrice.Drug;
  dosage_form: string;
  dosage: string;
  quantity: number;
  days_supply: number;
  location: FindBestPrice.Location;
}

export function pluralizeQuantity(quantity: string | number, packaging: string): string {
  const qty = Number(quantity);
  if (qty === 1 || !packaging) return `${qty} ${packaging}`.trim();

  const plural = IRREGULAR_PLURALS[packaging.toLowerCase()] || (packaging.endsWith("s") ? packaging : `${packaging}s`);
  return `${qty} ${plural}`;
}

export function findConfiguration(
  drug: FindBestPrice.Drug | undefined,
  dosageForm: string,
  dosage?: string,
): FindBestPrice.DrugConfiguration | undefined {
  return drug?.configurations?.find(
    (configuration) => configuration.dosage_form === dosageForm && (dosage === undefined || configuration.dosage === dosage),
  );
}

export function packagingLabel(drug: FindBestPrice.Drug | undefined, dosageForm: string): string {
  const configuration = findConfiguration(drug, dosageForm);
  return configuration?.dosage_form_packaging || configuration?.dosage_form || "";
}

export function uniqueSorted(values: Array<string | undefined>): string[] {
  const unique = Array.from(new Set(values.filter((value): value is string => !!value)));
  return unique.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
}

export function dosageFormOptions(drug: FindBestPrice.Drug | undefined): string[] {
  return uniqueSorted((drug?.configurations || []).map((configuration) => configuration.dosage_form));
}

export function dosageOptions(drug: FindBestPrice.Drug | undefined, dosageForm: string): string[] {
  return uniqueSorted(
    (drug?.configurations || [])
      .filter((configuration) => configuration.dosage_form === dosageForm)
      .map((configuration) => configuration.dosage),
  );
}

export function quantityOptions(drug: FindBestPrice.Drug | undefined, dosageForm: string, dosage: string): string[] {
  const quantities = findConfiguration(drug, dosageForm, dosage)?.display_quantities || [];
  return quantities.map((quantity) => String(quantity));
}

export function geolocateRequest(token: string, location: string): FindBestPrice.GeolocateRequest {
  const text = location.trim();
  return ZIP_REGEX.test(text) ? { token, zip_code: text } : { token, search_address: text };
}

export function formatDollars(value: string | number | undefined): string {
  const amount = Number(value ?? 0);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(isNaN(amount) ? 0 : amount);
}

export function formatDistance(distance: number | undefined): string | null {
  if (distance === undefined || distance === null) return null;
  const rounded = Math.round(Number(distance) * 100) / 100;
  return `${rounded} ${rounded === 1 ? "Mile" : "Miles"}`;
}

export function patientPayAmount(price: FindBestPrice.Price): string {
  return price.claim?.response?.response_pricing?.patient_pay_amount || "0";
}

export function planPayAmount(price: FindBestPrice.Price): string {
  return price.claim?.response?.response_pricing?.total_amount_paid || "0";
}

export function isPriceAvailable(price: FindBestPrice.Price): boolean {
  const status = price.claim?.response?.response_status?.transaction_response_status;
  const basis = price.claim?.response?.response_pricing?.basis_of_reimbursement_determination;
  return status === CLAIM_STATUS_PAID && basis !== BASIS_OF_REIMBURSEMENT_USUAL_AND_CUSTOMARY;
}

export function pharmacyAddress(pharmacy: FindBestPrice.Pharmacy): string {
  return [pharmacy.address_line_1, pharmacy.address_line_2].filter(Boolean).join(" ");
}

export function pharmacyCityStateZip(pharmacy: FindBestPrice.Pharmacy): string {
  const city = pharmacy.city ? `${pharmacy.city},` : "";
  return [city, pharmacy.state, pharmacy.zip].filter(Boolean).join(" ");
}

export function formatPhone(phone: string | undefined): string {
  const digits = (phone || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) return phone || "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function appleMapsUrl(pharmacy: FindBestPrice.Pharmacy): string {
  const query = encodeURIComponent(pharmacy.name || "");
  return `https://maps.apple.com/?q=${query}&ll=${pharmacy.latitude || ""},${pharmacy.longitude || ""}`;
}

export function yesNo(value: string | undefined): string | null {
  if (!value) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === "Y" || normalized === "YES") return "Yes";
  if (normalized === "N" || normalized === "NO") return "No";
  return value;
}
